/**
 * Email Templates — the registry every coordinator dashboard reads from.
 *
 * Publish a template here and it becomes selectable in every coordinator
 * dashboard the same minute, with no release. THAT IS THE WHOLE POINT of the
 * screen, and it is also the reason for the amount of prose on it: "publish"
 * has to mean exactly one thing, and it must not be mistaken for "send".
 *
 * ⚠️ A TEMPLATE IS NOT A SEND. Nothing on this page mails anybody. Publishing
 * changes what a coordinator can CHOOSE; a coordinator sending it is a separate
 * act inside their own dashboard, and the copy says so at every step.
 *
 * ⚠️ VERSIONS ARE ROWS AND PUBLISHED ONES ARE HISTORY. Editing a live version
 * is refused by the server (409 EDIT_PUBLISHED) — a coordinator may already
 * have copied it — so "Edit" on a live family starts the next DRAFT instead,
 * leaving the live one untouched until that draft is published.
 *
 * ⚠️ THE SEND STATISTICS ARE ALMOST ALWAYS '—', AND THAT IS CORRECT. They are
 * provider-level aggregates for the Brevo tag `template:<registry_key>`, and no
 * send path tags registry keys yet. A zero would claim a measured nothing; the
 * dash, with its tooltip, says there is no measurement.
 *
 * Mirrors the WeddingCataloguePage conventions: MUI table, page-owned Snackbar,
 * dialogs in their own files, every mutation confirm-gated.
 */

import React from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Snackbar, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmptyState from '../../common/EmptyState';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type {
  TemplateContract, TemplateFamily, TemplateVersion, TemplatesResponse,
} from '../weddingApi';
import { num, shortDate } from '../weddingFormat';
import TemplateDialog, { AUDIENCE_LABEL, KIND_LABEL, PUBLISHED_TOAST } from './TemplateDialog';

/** A live version published inside this window still wears the "New" pill. */
const NEW_FOR_DAYS = 30;

const STATS_TOOLTIP =
  'Not measured. These are the email provider’s aggregates for this template’s tag, and no send '
  + 'path tags registry keys yet — so there is nothing to report rather than a zero that looks like '
  + 'a measurement.';

const DRAFT_TOOLTIP = 'Draft — coordinators cannot see this';

type Toast = { severity: 'success' | 'error' | 'warning' | 'info'; message: string } | null;

type Confirm =
  | { kind: 'publish'; family: TemplateFamily }
  | { kind: 'retire'; family: TemplateFamily }
  | null;

function isNew(publishedAt: string | null): boolean {
  if (!publishedAt) return false;
  const at = new Date(publishedAt).getTime();
  if (Number.isNaN(at)) return false;
  return Date.now() - at < NEW_FOR_DAYS * 86400000;
}

/** A count the provider never reported renders '—', never 0. */
const Stat: React.FC<{ value: import('../weddingApi').Numeric | null | undefined }> = ({ value }) => {
  if (value === null || value === undefined) {
    return (
      <Tooltip title={STATS_TOOLTIP}>
        <Typography variant="body2" color="text.secondary">—</Typography>
      </Tooltip>
    );
  }
  return <Typography variant="body2">{num(value).toLocaleString()}</Typography>;
};

const WeddingTemplatesPage: React.FC = () => {
  const [data, setData] = React.useState<TemplatesResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<Toast>(null);
  const [confirm, setConfirm] = React.useState<Confirm>(null);
  const [busy, setBusy] = React.useState(false);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogDraft, setDialogDraft] = React.useState<TemplateVersion | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await weddingApi.getTemplates());
    } catch (e) {
      setError((e as WeddingApiError).message || 'Could not load the template registry');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const contract: TemplateContract = {
    audiences: data?.audiences || ['guests', 'quiet_guests', 'coordinators', 'couple', 'guests_platinum'],
    kinds: data?.kinds || ['invitation', 'reminder', 'thank_you'],
    statuses: data?.statuses || ['draft', 'live', 'superseded', 'retired'],
    merge_fields: data?.merge_fields || [],
    subject_max: data?.subject_max || 120,
    subject_soft_max: data?.subject_soft_max || 60,
    name_max: data?.name_max || 80,
    body_max: data?.body_max || 20000,
  };

  /** Fetches the full draft row — the list carries ids, not bodies. */
  const fetchDraft = async (family: TemplateFamily): Promise<TemplateVersion | null> => {
    const history = await weddingApi.getTemplateHistory(family.registry_key);
    const versions = history.versions || [];
    return (
      versions.find((v) => v.id === family.draft_id)
      || versions.find((v) => v.status === 'draft')
      || null
    );
  };

  const openCreate = () => {
    setDialogDraft(null);
    setDialogOpen(true);
  };

  const openEdit = async (family: TemplateFamily) => {
    setBusy(true);
    try {
      if (family.draft_id) {
        const draft = await fetchDraft(family);
        if (!draft) {
          setToast({ severity: 'error', message: 'That draft could not be loaded. Refresh and try again.' });
          return;
        }
        setDialogDraft(draft);
        setDialogOpen(true);
        return;
      }
      if (family.live_id) {
        // A published row is history: start the next draft rather than edit it.
        const result = await weddingApi.newTemplateVersion(family.live_id);
        setDialogDraft(result.template);
        setDialogOpen(true);
        setToast({
          severity: 'info',
          message: `Started version ${result.template.version}, copied from version ${result.copied_from_version}. The live version is unchanged until you publish.`,
        });
        return;
      }
      setToast({ severity: 'warning', message: 'This template has no draft and no live version to copy.' });
    } catch (e) {
      const err = e as WeddingApiError;
      if (err.code === 'DRAFT_EXISTS') {
        const draft = await fetchDraft(family).catch(() => null);
        if (draft) {
          setDialogDraft(draft);
          setDialogOpen(true);
          return;
        }
      }
      setToast({ severity: 'error', message: err.message || 'Could not open the template' });
    } finally {
      setBusy(false);
    }
  };

  const doPublish = async (family: TemplateFamily) => {
    if (!family.draft_id) return;
    setBusy(true);
    try {
      await weddingApi.publishTemplate(family.draft_id);
      setToast({ severity: 'success', message: PUBLISHED_TOAST });
      setConfirm(null);
      load();
    } catch (e) {
      setToast({ severity: 'error', message: (e as WeddingApiError).message || 'Could not publish' });
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const doRetire = async (family: TemplateFamily) => {
    if (!family.live_id) return;
    setBusy(true);
    try {
      const result = await weddingApi.retireTemplate(family.live_id);
      setToast({
        severity: 'success',
        message: `${family.name} retired. ${result.existing_copies_note}`,
      });
      setConfirm(null);
      load();
    } catch (e) {
      setToast({ severity: 'error', message: (e as WeddingApiError).message || 'Could not retire' });
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const templates = data?.templates || [];
  const counts = data?.counts;

  if (!loading && error && !data) {
    return (
      <Box>
        <EmptyState
          icon="error"
          title="Could not load the template registry"
          description={error}
          action={{ label: 'Retry', onClick: load, variant: 'contained' }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <MailOutlineIcon color="primary" />
        <Typography variant="h5">Email Templates</Typography>
        {counts && (
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={`${counts.live} live · ${counts.draft} draft${counts.draft === 1 ? '' : 's'}`}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          disabled={loading || data?.available === false}
        >
          New template
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every email a book can send. Publishing a template makes it selectable in every coordinator
        dashboard immediately — no release needed. It does not send or schedule anything.
      </Typography>

      {data?.available === false ? (
        <EmptyState
          icon="communications"
          title="The template registry is not in this database yet"
          description={
            data.not_migrated_reason
            || 'The migration that creates the registry has not been applied here, so there is nothing to list and nothing can be created.'
          }
        />
      ) : (
        <>
          {data && data.stats_available === false && (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              <AlertTitle>Send statistics are not available here</AlertTitle>
              {data.stats_note
                || 'No send path tags registry keys yet, so every send/open/click column reads “—”.'}
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Template</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Sent</TableCell>
                  <TableCell align="right">Opened</TableCell>
                  <TableCell align="right">Clicked</TableCell>
                  <TableCell align="right">Updated</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {loading
                          ? 'Loading…'
                          : 'No template has been created yet. The first one you publish becomes selectable in every coordinator dashboard.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : templates.map((family) => {
                  // Nothing live means no coordinator can see this family at
                  // all — that is what the dimming is about, not the presence
                  // of a draft alongside a published version.
                  const invisible = !family.live_id;
                  const draftOnly = invisible && family.latest_status === 'draft';
                  const row = (
                    <TableRow
                      key={family.registry_key}
                      data-testid={`template-row-${family.registry_key}`}
                      style={{ opacity: invisible ? 0.6 : 1 }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {family.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            v{family.live_version || family.latest_version}
                          </Typography>
                          {isNew(family.live_published_at) && (
                            <Tooltip title={`Published ${shortDate(family.live_published_at)}.`}>
                              <Chip size="small" color="secondary" variant="outlined" label="New" />
                            </Tooltip>
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {family.registry_key} · {KIND_LABEL[family.kind] || family.kind}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {AUDIENCE_LABEL[family.live_audience || family.audience]
                            || family.live_audience || family.audience}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {family.live_id && (
                            <Chip size="small" color="success" variant="outlined" label="Live" />
                          )}
                          {family.draft_id && (
                            <Tooltip title={DRAFT_TOOLTIP}>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Draft v${family.draft_version}`}
                              />
                            </Tooltip>
                          )}
                          {!family.live_id && !family.draft_id && (
                            <Chip size="small" variant="outlined" label={family.latest_status} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right"><Stat value={family.delivered} /></TableCell>
                      <TableCell align="right"><Stat value={family.unique_opens} /></TableCell>
                      <TableCell align="right"><Stat value={family.unique_clicks} /></TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {shortDate(family.last_touched_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button size="small" disabled={busy} onClick={() => openEdit(family)}>
                            {family.draft_id ? 'Edit draft' : 'New version'}
                          </Button>
                          {family.draft_id && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              onClick={() => setConfirm({ kind: 'publish', family })}
                            >
                              Publish
                            </Button>
                          )}
                          {family.live_id && (
                            <Button
                              size="small"
                              color="error"
                              disabled={busy}
                              onClick={() => setConfirm({ kind: 'retire', family })}
                            >
                              Retire
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                  return draftOnly ? (
                    <Tooltip key={family.registry_key} title={DRAFT_TOOLTIP} followCursor>
                      {row}
                    </Tooltip>
                  ) : row;
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {data?.book_count !== null && data?.book_count !== undefined && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              A published template becomes selectable in {data.book_count} book
              {data.book_count === 1 ? '' : 's'} immediately. Books that already copied an earlier
              version keep their own copy.
            </Typography>
          )}
        </>
      )}

      <TemplateDialog
        open={dialogOpen}
        draft={dialogDraft}
        contract={contract}
        onClose={() => { setDialogOpen(false); setDialogDraft(null); }}
        onSaved={(message) => {
          setDialogOpen(false);
          setDialogDraft(null);
          setToast({ severity: 'success', message });
          load();
        }}
        onPublished={() => {
          setDialogOpen(false);
          setDialogDraft(null);
          setToast({ severity: 'success', message: PUBLISHED_TOAST });
          load();
        }}
        onError={(message) => setToast({ severity: 'error', message })}
      />

      <Dialog open={confirm !== null} onClose={busy ? undefined : () => setConfirm(null)}>
        <DialogTitle>
          {confirm?.kind === 'retire'
            ? `Retire ${confirm.family.name}?`
            : 'Publish to every coordinator dashboard?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {confirm?.kind === 'retire' ? (
              <>
                “{confirm.family.name}” stops being offered to coordinators. Books that already
                added it keep their own copy, and nothing already sent is affected.
              </>
            ) : confirm ? (
              <>
                Draft version {confirm.family.draft_version} of “{confirm.family.name}” becomes the
                live version immediately, in every book, with no release. It does <strong>not</strong>{' '}
                send or schedule any email.
              </>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            color={confirm?.kind === 'retire' ? 'error' : 'primary'}
            disabled={busy}
            onClick={() => {
              if (!confirm) return;
              if (confirm.kind === 'publish') doPublish(confirm.family);
              else doRetire(confirm.family);
            }}
          >
            {confirm?.kind === 'retire' ? 'Retire' : 'Publish to all dashboards'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast !== null}
        autoHideDuration={8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default WeddingTemplatesPage;
